export type FieldType = "Number" | "Text" | "Dropdown";

export type JobType = "groundworks" | "roadsAndSewers";

// Placeholder a user can embed anywhere in a Unit Conversion column's Table
// Column name — a run of 2+ "x" characters (e.g. "depth n.e. xxmm", "depth
// n.e. xxxmm", "Yield XXX") — that Configure Headers substitutes with the
// number entered there. Matching is case-insensitive and requires 2+ in a
// row so ordinary words with an incidental single "x" (e.g. "Excavate") are
// never mistaken for it.
const UNIT_CONVERSION_PLACEHOLDER_REGEX = /x{2,}/i;

export function hasUnitConversionPlaceholder(tableColumn: string): boolean {
  return UNIT_CONVERSION_PLACEHOLDER_REGEX.test(tableColumn);
}

export function replaceUnitConversionPlaceholder(tableColumn: string, value: number): string {
  return tableColumn.replace(UNIT_CONVERSION_PLACEHOLDER_REGEX, String(value));
}

export interface TableConfigRow {
  id: string;
  tableColumn: string;
  evaluateCode: string;
  // Generic gating field for sections whose Category options depend on
  // another per-row choice (e.g. Drainage's Supplier, Plot Drives' Drive
  // Stage) — see sectionCategoryConfig.ts's `variantColumn` config.
  variant: string;
  category: string;
  subCategory: string;
  fieldType: FieldType;
  isDynamic: boolean;
  unitConversion: boolean;
  // Number entered in Configure Headers that substitutes the "xxx"-style
  // placeholder in `tableColumn` when displaying this column — `tableColumn`
  // itself stays as the original template (e.g. "depth n.e. xxxmm") so
  // Configure Headers can keep showing/editing it.
  unitConversionValue: number;
  options: string;
  takeoffImportName: string;
}

export interface PreviewColumnConfig {
  rowId: string;
  tableColumn: string;
  evaluateCode: string;
  variant: string;
  fieldType: FieldType;
  isDynamic: boolean;
  unitConversion: boolean;
  unitConversionValue: number;
  options?: string[];
  takeoffImportName: string;
}

export interface PreviewSubCategoryConfig {
  subCategory: string;
  tableColumns: PreviewColumnConfig[];
}

export interface PreviewCategoryConfig {
  category: string;
  subCategories: PreviewSubCategoryConfig[];
}

export interface SectionConfig {
  jobType: JobType;
  section: string;
  excelSheetName: string | null;
  categories: PreviewCategoryConfig[];
}

function toPreviewColumn(row: TableConfigRow): PreviewColumnConfig {
  return {
    rowId: row.id,
    tableColumn: row.tableColumn,
    evaluateCode: row.evaluateCode,
    variant: row.variant,
    fieldType: row.fieldType,
    isDynamic: row.isDynamic,
    unitConversion: row.unitConversion,
    unitConversionValue: row.unitConversionValue,
    options:
      row.isDynamic || row.fieldType === "Dropdown"
        ? row.options
            .split(",")
            .map((option) => option.trim())
            .filter(Boolean)
        : undefined,
    takeoffImportName: row.takeoffImportName,
  };
}

export function buildSectionConfig(
  jobType: JobType,
  section: string,
  rows: TableConfigRow[],
  excelSheetName: string,
): SectionConfig {
  const categoryOrder: string[] = [];
  const categoryGroups = new Map<string, { subOrder: string[]; subRows: Map<string, TableConfigRow[]> }>();

  rows.forEach((row) => {
    const category = row.category.trim() || "Uncategorized";
    const subCategory = row.subCategory.trim() || "General";

    if (!categoryGroups.has(category)) {
      categoryOrder.push(category);
      categoryGroups.set(category, { subOrder: [], subRows: new Map() });
    }
    const group = categoryGroups.get(category)!;

    if (!group.subRows.has(subCategory)) {
      group.subOrder.push(subCategory);
      group.subRows.set(subCategory, []);
    }
    group.subRows.get(subCategory)!.push(row);
  });

  return {
    jobType,
    section,
    excelSheetName: excelSheetName || null,
    categories: categoryOrder.map((category) => {
      const group = categoryGroups.get(category)!;
      return {
        category,
        subCategories: group.subOrder.map((subCategory) => ({
          subCategory,
          tableColumns: group.subRows.get(subCategory)!.map(toPreviewColumn),
        })),
      };
    }),
  };
}
